import React, { useEffect } from 'react';
import {BrowserRouter, Routes, Route, Navigate, useLocation} from "react-router-dom";
import LoginPage from "./pages/login";
import DashboardPage from "./pages/dashboard_page";
import MyPage from "./pages/my_page";
import HistoryPage from "./pages/history_page";
import BacktestPage from "./pages/backtest_page";
import ExchangeNotiPage from "./pages/exchange_noti_page";
import MaintenancePage from "./pages/maintenance_page";
import NotFoundPage from "./pages/not_found_page";
import PaperOverviewPage from "./pages/paper_overview_page";
import PaperStrategyDetailPage from "./pages/paper_strategy_detail_page";
import PaperHealthPage from "./pages/paper_health_page";
import PaperRetiredPage from "./pages/paper_retired_page";
import ServerPage from "./pages/server_page";
import {
    clearLoginSession,
    getAccessToken,
    getRefreshToken,
    isTokenExpired,
    markSessionExpired,
} from "./utils/auth";
import { bootstrapSession } from "./services/apiClient";
import ErrorBoundary from "./components/common/error_boundary";
import ToastProvider from "./contexts/toast_context";
import ConfirmDialogProvider from "./components/common/confirm_dialog";
import { VariantProvider } from "./contexts/variant_context";

const SessionWatcher = () => {
    const location = useLocation();

    useEffect(() => {
        const checkSession = () => {
            const access = getAccessToken();
            const refresh = getRefreshToken();

            if (!access && !refresh) {
                if (location.pathname !== "/login") {
                    markSessionExpired();
                    clearLoginSession();
                    window.location.replace("/login");
                }
                return;
            }

            if (refresh && isTokenExpired(refresh, 30)) {
                markSessionExpired();
                clearLoginSession();
                if (location.pathname !== "/login") {
                    window.location.replace("/login");
                }
                return;
            }

            if (!refresh && access && isTokenExpired(access, 30)) {
                markSessionExpired();
                clearLoginSession();
                if (location.pathname !== "/login") {
                    window.location.replace("/login");
                }
            }
        };

        checkSession();
        const intervalId = setInterval(checkSession, 60000);
        return () => clearInterval(intervalId);
    }, [location.pathname]);

    return null;
};

/**
 * 콜드 스타트 세션 복구 게이트. access 토큰이 localStorage 에 없으면(iOS standalone
 * PWA 가 백그라운드 회수로 비운 경우 포함) /api/user/token/bootstrap 으로 httpOnly
 * refresh 쿠키 기반 복구를 1회 시도한 뒤 라우터를 렌더한다. access 토큰이 이미 있으면
 * 즉시 통과 — 정상/데스크톱 경로는 동작·지연 변화 없음.
 */
const useColdStartBootstrap = () => {
    const [booting, setBooting] = React.useState(() => {
        if (getAccessToken()) return false; // 정상 경로: 복구 불필요
        if (window.location.pathname === "/login") return false; // 로그인 화면은 그대로
        return true;
    });
    useEffect(() => {
        if (!booting) return undefined;
        let alive = true;
        bootstrapSession().finally(() => {
            if (alive) setBooting(false);
        });
        return () => {
            alive = false;
        };
    }, [booting]);
    return booting;
};

export default function App() {
    const booting = useColdStartBootstrap();
    if (booting) {
        // 복구 시도 중 짧은 빈 화면 (깜빡임 방지). 실패 시 아래 라우터가 /login 으로 보냄.
        return <div className="min-h-screen bg-surface" role="status" aria-busy="true" aria-label="페이지 로딩 중" />;
    }
    return (
        <ErrorBoundary>
            <VariantProvider>
            <ToastProvider>
                <ConfirmDialogProvider>
                    <BrowserRouter>
                        <SessionWatcher />
                        <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />}/>  {/* AUTH: public (redirect-only) */}
                            <Route path="/dashboard" element={<DashboardPage/>}/>  {/* AUTH: required */}
                            <Route path='/history' element={<HistoryPage/>}/>  {/* AUTH: required */}
                            <Route path="/my_page" element={<MyPage/>}/>  {/* AUTH: required */}
                            <Route path="/backtest" element={<BacktestPage/>}/>  {/* AUTH: required */}
                            <Route path="/exchange-noti" element={<ExchangeNotiPage/>}/>  {/* AUTH: required */}
                            <Route path="/maintenance" element={<MaintenancePage/>}/>  {/* AUTH: public (unauth allowed for viewing) */}
                            <Route path="/research" element={<PaperOverviewPage/>}/>  {/* AUTH: required — DAS-122 paper trade 라이브 모니터링 */}
                            <Route path="/research/retired" element={<PaperRetiredPage/>}/>  {/* AUTH: required — 페기 전략 카탈로그 */}
                            <Route path="/research/strategies/:id" element={<PaperStrategyDetailPage/>}/>  {/* AUTH: required — DAS-122 */}
                            <Route path="/research/health" element={<PaperHealthPage/>}/>  {/* AUTH: required — DAS-122 */}
                            <Route path="/server" element={<ServerPage/>}/>  {/* AUTH: required — DAS-129 서버 리소스 (최상위) */}
                            <Route path="/stocks" element={<Navigate to="/dashboard" replace />}/>  {/* DAS-164: /stocks 폐지 — 대시보드 미국주식 탭으로 흡수, 구 북마크 리다이렉트 */}
                            <Route path="/research/resources" element={<Navigate to="/server" replace />}/>  {/* 구 경로 호환 리다이렉트 */}
                            <Route path="/login" element={<LoginPage/>}/>  {/* AUTH: public (UNAUTH ENTRY — do NOT call requireLoginOrThrow) */}
                            <Route path="*" element={<NotFoundPage/>}/>  {/* DAS-70 Wave-A P0-2: catch-all 404 */}
                        </Routes>
                    </BrowserRouter>
                </ConfirmDialogProvider>
            </ToastProvider>
            </VariantProvider>
        </ErrorBoundary>
    );
}
